import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, ChevronDown } from "lucide-react";
import { todayIST } from "@/lib/utils";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const MODES = ["NEFT", "RTGS", "Cheque", "Cash", "UPI", "Other"];
export const EXPENSE_CATEGORIES = [
  ["labour", "Labour"], ["transport", "Transport / logistics"], ["civil", "Civil works"], ["material", "Own material (no PO)"],
  ["liaison", "Liaison / approvals"], ["testing", "Testing & commissioning"], ["rental", "Equipment rental"], ["travel", "Travel"], ["misc", "Miscellaneous"],
];
export const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
export const fmtDec = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
export const today = () => todayIST();

export const inputCls = "w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white";
export const labelCls = "block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5";

export function Select({ value, onChange, children, disabled, testid }) {
  return (
    <div className="relative">
      <select value={value ?? ""} onChange={onChange} disabled={disabled} data-testid={testid}
        className={inputCls + " appearance-none pr-8 disabled:bg-[#F8FAFC]"}>
        {children}
      </select>
      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
    </div>
  );
}

export function Modal({ title, onClose, children, footer, testid, wide }) {
  // Rendered into document.body so page wrappers (animations/transforms) can never offset the overlay.
  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} animate-fade-up flex flex-col max-h-[92vh]`} data-testid={testid}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0] shrink-0">
          <h3 className="font-heading font-bold text-base text-[#0A1128]">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F8FAFC] text-[#64748B]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#E2E8F0] shrink-0">{footer}</div>
      </div>
    </div>,
    document.body
  );
}

/** Vendor payment: against a PO (auto-fills vendor & project) or free (project + vendor name). */
export function VendorPaymentModal({ projects, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial || { project_id: "", po_id: "", vendor_name: "", amount: "", date: today(), mode: "NEFT", reference: "", notes: "", tds_amount: "" });
  const [pos, setPos] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!form.project_id) { setPos([]); return; }
    axios.get(`${API}/financials/po`, { params: { project_id: form.project_id } })
      .then(r => setPos((r.data || []).filter(p => p.status !== "void")))
      .catch(() => setPos([]));
  }, [form.project_id]);

  const selectedPo = pos.find(p => p.id === form.po_id);
  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!form.project_id) { toast.error("Select a project"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.po_id && !form.vendor_name?.trim()) { toast.error("Pick a PO or enter the vendor name"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: amt, po_id: form.po_id || null, tds_amount: parseFloat(form.tds_amount) || 0 };
      const { data } = isEdit
        ? await axios.put(`${API}/ledger/vendor-payments/${initial.id}`, payload)
        : await axios.post(`${API}/ledger/vendor-payments`, payload);
      toast.success(isEdit ? "Vendor payment updated" : `Paid ${fmtDec(amt)} to ${data.vendor_name}`);
      onSaved(data, isEdit); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  return (
    <Modal title={isEdit ? "Edit Vendor Payment" : "Record Payment to Vendor"} onClose={onClose} testid="vendor-payment-modal"
      footer={<>
        <button onClick={onClose} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B]">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="vendor-payment-save" className="px-4 py-2 bg-[#0A1128] text-white rounded text-sm font-bold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
      </>}>
      <div>
        <label className={labelCls}>Project *</label>
        <Select value={form.project_id} onChange={e => { set("project_id", e.target.value); set("po_id", ""); }} disabled={isEdit} testid="vp-project">
          <option value="">— Select Project —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>
      <div>
        <label className={labelCls}>Against PO (optional)</label>
        <Select value={form.po_id} onChange={e => { const po = pos.find(p => p.id === e.target.value); set("po_id", e.target.value); if (po) set("vendor_name", po.vendor_name); }} disabled={!form.project_id} testid="vp-po">
          <option value="">— No PO (ad-hoc vendor payment) —</option>
          {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} · {po.vendor_name} · {fmt(po.total)}{po.paid_amount ? ` (paid ${fmt(po.paid_amount)})` : ""}</option>)}
        </Select>
        {selectedPo && <p className="text-[10px] text-[#8AA496] mt-1">Balance on this PO: {fmt((selectedPo.total || 0) - (selectedPo.paid_amount || 0))}</p>}
      </div>
      <div>
        <label className={labelCls}>Vendor *</label>
        <input value={form.vendor_name || ""} onChange={e => set("vendor_name", e.target.value)} className={inputCls} placeholder="Vendor name" disabled={!!form.po_id} data-testid="vp-vendor" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Date *</label><input type="date" value={form.date} onChange={e => set("date", e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Amount (₹) *</label><input type="number" min="1" value={form.amount} onChange={e => set("amount", e.target.value)} className={inputCls} data-testid="vp-amount" /></div>
        <div><label className={labelCls}>Mode</label><Select value={form.mode} onChange={e => set("mode", e.target.value)}>{MODES.map(m => <option key={m}>{m}</option>)}</Select></div>
        <div><label className={labelCls}>TDS deducted (₹)</label><input type="number" min="0" value={form.tds_amount ?? ""} onChange={e => set("tds_amount", e.target.value)} className={inputCls} placeholder="0" /></div>
      </div>
      <div><label className={labelCls}>Reference (UTR / cheque no.)</label><input value={form.reference || ""} onChange={e => set("reference", e.target.value)} className={inputCls} /></div>
      <div><label className={labelCls}>Notes</label><input value={form.notes || ""} onChange={e => set("notes", e.target.value)} className={inputCls} /></div>
    </Modal>
  );
}

export function ExpenseModal({ projects, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial || { project_id: "", category: "labour", description: "", payee: "", amount: "", date: today(), mode: "Cash", reference: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!form.project_id) { toast.error("Select a project"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: amt };
      const { data } = isEdit
        ? await axios.put(`${API}/ledger/expenses/${initial.id}`, payload)
        : await axios.post(`${API}/ledger/expenses`, payload);
      toast.success(isEdit ? "Expense updated" : `Expense of ${fmtDec(amt)} recorded`);
      onSaved(data, isEdit); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };
  return (
    <Modal title={isEdit ? "Edit Expense" : "Record Project Expense"} onClose={onClose} testid="expense-modal"
      footer={<>
        <button onClick={onClose} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B]">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="expense-save" className="px-4 py-2 bg-[#0A1128] text-white rounded text-sm font-bold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
      </>}>
      <div>
        <label className={labelCls}>Project *</label>
        <Select value={form.project_id} onChange={e => set("project_id", e.target.value)} disabled={isEdit} testid="exp-project">
          <option value="">— Select Project —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Category *</label><Select value={form.category} onChange={e => set("category", e.target.value)} testid="exp-category">{EXPENSE_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        <div><label className={labelCls}>Paid to</label><input value={form.payee || ""} onChange={e => set("payee", e.target.value)} className={inputCls} placeholder="Contractor / person / shop" /></div>
        <div><label className={labelCls}>Date *</label><input type="date" value={form.date} onChange={e => set("date", e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Amount (₹) *</label><input type="number" min="1" value={form.amount} onChange={e => set("amount", e.target.value)} className={inputCls} data-testid="exp-amount" /></div>
        <div><label className={labelCls}>Mode</label><Select value={form.mode} onChange={e => set("mode", e.target.value)}>{MODES.map(m => <option key={m}>{m}</option>)}</Select></div>
        <div><label className={labelCls}>Reference</label><input value={form.reference || ""} onChange={e => set("reference", e.target.value)} className={inputCls} /></div>
      </div>
      <div><label className={labelCls}>Description</label><input value={form.description || ""} onChange={e => set("description", e.target.value)} className={inputCls} placeholder="What was this for?" /></div>
    </Modal>
  );
}
